/**
 * Pure, framework-free helpers for recovering from Livewire 419 ("Page Expired")
 * responses. A 419 means the page's CSRF/session token is no longer valid -
 * typically after a deploy, or a tab left open past the session lifetime - after
 * which every Livewire request (especially background wire:poll commits) keeps
 * failing. app.js uses these to quietly reload for a fresh token while preserving
 * the user's typed input, and to report the expiry to Honeybadger once per page
 * load rather than once per failed poll.
 *
 * Everything here is side-effect-free so it can be unit tested under `node --test`
 * without a browser or build step.
 */

export const EXPIRED_INPUT_STORAGE_KEY = 'lw419:preserved-input';
export const EXPIRED_RELOAD_GUARD_KEY = 'lw419:last-reload-at';

/** How long after a 419-driven reload we refuse to reload again (loop guard). */
const RELOAD_COOLDOWN_MS = 12000;

/** How long a preserved-input stash stays valid for restoration after a reload. */
export const PRESERVED_INPUT_TTL_MS = 120000;

/** Input types we must never stash in web storage or auto-restore. */
const NON_RESTORABLE_TYPES = new Set([
    'password', 'file', 'hidden', 'submit', 'button', 'reset', 'checkbox', 'radio',
]);

/**
 * wire:model variants we read a field's bound property name from. Timed
 * modifiers (wire:model.debounce.500ms, .throttle.500ms) aren't enumerable, but
 * such fields are matched by id/name first - Filament fields always carry an id -
 * so these common modifiers are a sufficient fallback.
 */
const WIRE_MODEL_ATTRS = [
    'wire:model',
    'wire:model.live',
    'wire:model.blur',
    'wire:model.lazy',
    'wire:model.defer',
];

function getAttr(el, name) {
    return typeof el.getAttribute === 'function' ? el.getAttribute(name) : null;
}

function isRestorableField(el) {
    if (!el || typeof el.tagName !== 'string') {
        return false;
    }
    const tag = el.tagName.toLowerCase();
    if (tag !== 'input' && tag !== 'textarea' && tag !== 'select') {
        return false;
    }
    if (typeof el.hasAttribute === 'function' && el.hasAttribute('data-no-419-restore')) {
        return false;
    }
    const type = (getAttr(el, 'type') || el.type || (tag === 'input' ? 'text' : tag)).toLowerCase();
    return ! NON_RESTORABLE_TYPES.has(type);
}

/**
 * Build a stable key for a field so a value stashed before reload can be matched
 * back to the same field afterwards. Prefers id, then name, then wire:model
 * (Filament renders fields with both an id and wire:model="data.*").
 */
export function fieldKey(el) {
    const id = getAttr(el, 'id');
    if (id) {
        return `id:${id}`;
    }
    const name = getAttr(el, 'name');
    if (name) {
        return `name:${name}`;
    }
    for (const attr of WIRE_MODEL_ATTRS) {
        const model = getAttr(el, attr);
        if (model) {
            return `model:${model}`;
        }
    }
    return null;
}

function eachField(root, callback) {
    if (!root || typeof root.querySelectorAll !== 'function') {
        return;
    }
    const els = root.querySelectorAll('input, textarea, select');
    if (!els || typeof els.forEach !== 'function') {
        return;
    }
    els.forEach((el) => {
        if (isRestorableField(el)) {
            callback(el);
        }
    });
}

/**
 * Snapshot non-empty field values keyed by fieldKey(). Empty fields are skipped
 * so we never overwrite freshly-rendered values with blanks on restore.
 *
 * @returns {Record<string, string>}
 */
export function collectRestorableInputs(root) {
    const out = {};
    eachField(root, (el) => {
        const key = fieldKey(el);
        if (! key) {
            return;
        }
        const value = el.value ?? '';
        if (value === '') {
            return;
        }
        out[key] = value;
    });
    return out;
}

/**
 * Re-apply stashed values to matching fields that are currently empty, firing
 * input/change so Livewire and Alpine pick up the restored values.
 *
 * @returns {number} count of fields restored
 */
export function applyRestorableInputs(root, saved) {
    if (! saved || typeof saved !== 'object') {
        return 0;
    }
    let restored = 0;
    eachField(root, (el) => {
        const key = fieldKey(el);
        if (! key || ! Object.prototype.hasOwnProperty.call(saved, key)) {
            return;
        }
        if ((el.value ?? '') !== '') {
            return;
        }
        el.value = saved[key];
        // <select> silently no-ops when the saved value doesn't match any
        // currently-rendered <option> (e.g. options populated by Alpine/Livewire
        // after mount). Without this check we'd dispatch input/change with an
        // empty value, clobbering whatever Livewire had stored server-side.
        if ((el.value ?? '') !== saved[key]) {
            return;
        }
        if (typeof el.dispatchEvent === 'function' && typeof Event === 'function') {
            el.dispatchEvent(new Event('input', { bubbles: true }));
            el.dispatchEvent(new Event('change', { bubbles: true }));
        }
        restored += 1;
    });
    return restored;
}

/**
 * Decide whether it's safe to reload in response to a 419. Returns false only
 * when we reloaded for a 419 within the cooldown window, which prevents reload
 * loops if the replacement page is itself expired.
 */
export function shouldReloadForExpiry(now, lastReloadAt, cooldownMs = RELOAD_COOLDOWN_MS) {
    if (lastReloadAt == null) {
        return true;
    }
    const delta = Number(now) - Number(lastReloadAt);
    if (! Number.isFinite(delta)) {
        return true;
    }
    return delta > cooldownMs;
}

/** Parse the (possibly stringified) snapshot of the first component in a payload. */
export function parseSnapshot(payload) {
    try {
        const snapshot = payload?.components?.[0]?.snapshot;
        if (snapshot == null) {
            return null;
        }
        return typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
    } catch (e) {
        if (! (e instanceof SyntaxError)) {
            throw e;
        }
        return null;
    }
}

/** Extract the path of a URL, used to fingerprint 419s when the component is unknown. */
export function pagePathFrom(url) {
    if (typeof url !== 'string' || url === '') {
        return 'unknown';
    }
    try {
        return new URL(url).pathname || '/';
    } catch (e) {
        if (! (e instanceof TypeError)) {
            throw e;
        }
        return 'unknown';
    }
}

/**
 * Honeybadger fingerprint for a 419. Falls back to the URL path when the
 * component name can't be recovered, so per-page expiries stop collapsing into
 * a single "unknown" fault and become triageable.
 */
export function fingerprintFor(component, url) {
    return `livewire:419:${component || pagePathFrom(url)}`;
}

/**
 * Assemble the diagnostic context attached to the Honeybadger notice.
 *
 * @returns {{
 *   component: (string|null),
 *   component_id: string,
 *   snapshot_release: string,
 *   url: (string|null),
 *   user_agent: (string|null),
 *   timestamp: string,
 *   response_preview: (string|null),
 * }}
 */
export function buildDiagnostic(payload, { url = null, userAgent = null, content = null, timestamp = null } = {}) {
    const memo = parseSnapshot(payload)?.memo ?? {};
    return {
        component: memo.name ?? null,
        component_id: memo.id ?? 'unknown',
        snapshot_release: memo.release ?? 'unknown',
        url,
        user_agent: userAgent,
        timestamp: timestamp ?? new Date().toISOString(),
        response_preview: typeof content === 'string' ? content.slice(0, 300) : null,
    };
}
